/**
 * ============================================================================
 * Role 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Role 模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createRoleApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createRoleApi } from '@bayes-mes/shared/api/role/factory'
 * export const roleApi = createRoleApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const result = await roleApi.getRoleList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createRoleApi } from '@bayes-mes/shared/api/role/factory'
 * export const roleApi = createRoleApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/role/factory
 * @description Role 模块 API 工厂函数
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-10-18
 * @last-updated 2025-10-18
 * 
 * @change-log
 * - 2025-10-18: 初始创建，实现所有基础 CRUD 方法和权限管理方法
 * ============================================================================
 */

// ===== 导入类型定义 =====
import type {
  Role,                      // 角色主实体类型
  RoleQueryParams,          // 列表查询参数
  CreateRoleForm,           // 创建表单
  UpdateRoleForm,           // 更新表单
  AssignPermissionsForm,    // 分配权限表单
} from '../../types/role'

// ===== 导入通用类型 =====
import type { PaginationResult } from '../../types/api'

// ===== 导入统一的请求客户端接口 =====
// ⚠️ 重要：所有工厂函数都从这里导入接口，不要重复定义！
import type { IRequestClient } from '../core/client'

// ===== 导入 API 路由定义 =====
import { API_ROUTES } from '../routes'

// ===== 导出工厂函数 =====
/**
 * 创建 Role 模块的 API 方法集合
 * 
 * 这是一个工厂函数，它接收一个"请求客户端"作为参数，
 * 返回一个包含所有 Role 相关 API 方法的对象。
 * 
 * @param client - 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 Role 相关的请求方法
 * 
 * 为什么这样设计？
 * - 解耦：API 逻辑与具体的请求实现分离
 * - 复用：同一套 API 定义可以在 Web 和 App 端使用
 * - 测试：可以注入 Mock 客户端进行单元测试
 */
export function createRoleApi(client: IRequestClient) {
  return {
    // ===== 基础 CRUD 操作 =====
    
    /**
     * 获取角色列表（分页）
     * 
     * 使用场景：
     * - 在角色管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能
     * - 用户可以根据角色名称、状态、系统角色等条件查询数据
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *     示例：page: 1（第一页），page: 2（第二页）
     *   - per_page?: number | 'all' - 每页数量，默认 15，可传 'all' 获取所有
     *     示例：per_page: 20（每页20条）, per_page: 'all'（获取所有）
     *   - name?: string - 角色名称，模糊搜索
     *     示例：name: '管理员'（搜索包含"管理员"的角色）
     *   - keywords?: string - 搜索关键词，搜索 name 或 description
     *     示例：keywords: '部门'（搜索名称或描述中包含"部门"的角色）
     *   - status?: number - 状态筛选（0=禁用，1=启用）
     *     示例：status: 1（只显示启用的角色）
     *   - is_system?: number - 是否系统角色（0=否，1=是）
     *     示例：is_system: 0（只显示非系统角色）
     *   - sort_by?: string - 排序字段
     *     示例：sort_by: 'sort_order'（按排序字段排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     *     示例：sort_order: 'asc'（升序）
     * 
     * @returns Promise<PaginationResult<Role>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: Role[],            // 角色列表数组，每个元素是一个角色对象
     *     total: number,           // 总记录数，如 25（共有25个角色）
     *     current_page: number,    // 当前页码，如 1（正在查看第1页）
     *     per_page: number,        // 每页数量，如 15（每页显示15条）
     *     last_page: number,       // 最后一页页码，如 2（总共2页）
     *     from: number,            // 当前页起始记录号，如 1（从第1条开始）
     *     to: number               // 当前页结束记录号，如 15（到第15条结束）
     *   }
     * 
     * 数据流向（完整过程）：
     * 1. 用户操作：用户在角色管理页面上点击"下一页"按钮或输入搜索关键词
     * 2. 前端调用：组件调用 roleApi.getRoleList({ page: 2, keywords: '管理' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/roles?page=2&keywords=管理
     * 5. 后端处理：
     *    - 路由匹配到 RoleController@index 方法
     *    - 验证查询参数（RoleQueryParams）
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 RoleResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<Role> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await roleApi.getRoleList()
     * console.log(page1.data)  // Role对象数组
     * console.log(page1.total) // 总记录数
     * 
     * // 示例2：获取第二页，每页20条
     * const page2 = await roleApi.getRoleList({
     *   page: 2,
     *   per_page: 20
     * })
     * 
     * // 示例3：搜索功能
     * const searchResult = await roleApi.getRoleList({
     *   keywords: '管理员'
     * })
     * 
     * // 示例4：组合查询（分页 + 搜索 + 筛选）
     * const result = await roleApi.getRoleList({
     *   page: 1,
     *   per_page: 10,
     *   status: 1,          // 只显示启用的
     *   is_system: 0,       // 只显示非系统角色
     *   sort_by: 'sort_order',
     *   sort_order: 'asc'
     * })
     * 
     * // 示例5：获取所有角色（不分页）
     * const all = await roleApi.getRoleList({
     *   per_page: 'all'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getRoleList(params?: RoleQueryParams) {
      // 调用请求客户端的 get 方法
      // - 第一个参数：从 routes.ts 引用的 URL 路径
      // - 第二个参数：配置对象，{ params } 会被转换为 URL 查询参数
      // - 泛型参数：指定返回数据的类型
      return client.get<PaginationResult<Role>>(API_ROUTES.role.list, { params })
    },
    
    /**
     * 获取角色详情
     * 
     * 使用场景：
     * - 在角色详情页面显示完整信息
     * - 编辑角色时获取当前数据
     * - 查看角色的详细资料和关联信息
     * 
     * @param id - 角色的唯一标识符（主键）
     *   示例：id: '123'（获取 ID 为 123 的角色）
     * 
     * @returns Promise<Role> 单个角色对象
     *   返回的数据结构：
     *   {
     *     id: string,              // 角色 ID
     *     name: string,            // 角色名称
     *     description: string,     // 角色描述
     *     guardName: string,       // 守卫名称
     *     isSystem: number,        // 是否系统角色（0=否，1=是）
     *     isSystemText: string,    // 是否系统角色文本（"是"/"否"）
     *     status: number,          // 状态（0=禁用，1=启用）
     *     statusText: string,      // 状态文本（"启用"/"禁用"）
     *     sortOrder: number,       // 排序
     *     dataScope: object,       // 数据权限配置
     *     dataScopeText: string,   // 数据权限范围文本
     *     userCount?: number,      // 用户数量（如果加载了关联）
     *     permissionCount?: number,// 权限数量（如果加载了关联）
     *     permissions?: string[],  // 权限ID数组（如果加载了关联）
     *     createdAt: string,       // 创建时间（ISO 8601格式）
     *     updatedAt: string        // 更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行，或在 URL 中输入详情页地址
     * 2. 前端调用：roleApi.getRoleDetail('123')
     * 3. 发送请求：GET /api/v1/roles/123
     * 4. 后端处理：RoleController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录
     *    - 如果不存在，返回 404
     *    - 通过 RoleResource 格式化数据
     * 5. 返回数据：单个 Role 对象
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的角色
     * const detail = await roleApi.getRoleDetail('123')
     * console.log(detail.name)  // 角色名称
     * console.log(detail.dataScopeText)  // 数据权限范围文本
     * 
     * // 在详情页组件中使用
     * const { id } = useRoute().params
     * const roleData = await roleApi.getRoleDetail(id as string)
     * ```
     * 
     * 错误处理：
     * - 404：角色不存在
     * - 403：无权限访问该角色
     * - 422：ID 格式错误
     */
    getRoleDetail(id: string) {
      // 使用函数式路由，自动拼接 ID
      // routes.ts 中定义：detail: (role) => `/api/v1/roles/${role}`
      return client.get<Role>(API_ROUTES.role.detail(id))
    },
    
    /**
     * 创建新角色
     * 
     * 使用场景：
     * - 在角色管理页面点击"新建"按钮
     * - 提交创建角色的表单
     * - 初始化系统时创建默认角色
     * 
     * @param data - 创建角色的表单数据
     *   字段要求（参考 CreateRoleForm 类型）：
     *   - name: string - 角色名称（必填，最大50字符，唯一）
     *   - description?: string - 角色描述（可选，最大500字符）
     *   - status?: number - 状态（可选，默认1启用）
     *   - sort_order?: number - 排序（可选，默认0）
     *   - data_scope?: object - 数据权限配置（可选）
     *   - permission_ids?: number[] - 权限ID列表（可选）
     * 
     * @returns Promise<Role> 创建成功后的角色对象（包含生成的 ID）
     *   返回的数据：
     *   {
     *     id: string,          // 自动生成的 ID
     *     name: string,        // 提交的名称
     *     description: string, // 提交的描述
     *     // ... 其他字段
     *     createdAt: string,   // 服务器生成的创建时间
     *     updatedAt: string    // 服务器生成的更新时间
     *   }
     * 
     * 数据流向：
     * 1. 用户填写创建表单，点击"提交"按钮
     * 2. 前端验证：表单验证（必填项、格式等）
     * 3. 前端调用：roleApi.createRole({ name: '部门经理', description: '...' })
     * 4. 发送请求：POST /api/v1/roles
     *    请求体：{ name: '部门经理', ... }（JSON格式）
     * 5. 后端处理：RoleController@store 方法
     *    - 验证数据（使用 CreateRoleRequest）
     *    - 检查唯一性（角色名称不能重复）
     *    - 插入数据库，自动设置 guard_name 为 'web'
     *    - 如果有 permission_ids，分配权限
     *    - 返回新创建的记录（包含自动生成的 ID 和时间戳）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到列表页或详情页
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 创建新角色
     * const newData: CreateRoleForm = {
     *   name: '部门经理',
     *   description: '负责部门日常管理',
     *   status: 1,
     *   sort_order: 10,
     *   data_scope: {
     *     scope: 4  // 本部门及以下数据权限
     *   },
     *   permission_ids: [1, 2, 3]
     * }
     * 
     * const created = await roleApi.createRole(newData)
     * console.log(created.id)  // 新创建的 ID
     * 
     * // 在表单提交中使用
     * const handleSubmit = async (formData: CreateRoleForm) => {
     *   try {
     *     const result = await roleApi.createRole(formData)
     *     message.success('创建成功')
     *     router.push(`/role/${result.id}`)  // 跳转到详情页
     *   } catch (error) {
     *     message.error('创建失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     *   - 必填字段缺失
     *   - 字段格式错误
     *   - 角色名称重复（违反唯一性约束）
     * - 403：无权限创建
     * - 500：服务器错误（数据库插入失败）
     */
    createRole(data: CreateRoleForm) {
      // POST 请求，第二个参数是请求体数据
      // 后端会自动将 snake_case 字段转换为数据库字段
      return client.post<Role>(API_ROUTES.role.create, data)
    },
    
    /**
     * 更新角色
     * 
     * 使用场景：
     * - 在角色编辑页面修改后提交
     * - 更新角色的部分字段
     * - 修改角色的数据权限配置
     * 
     * @param id - 要更新的角色的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   字段要求（参考 UpdateRoleForm 类型）：
     *   - 可以只包含部分字段（部分更新）
     *   - 不能修改的字段：id, createdAt（后端会忽略）
     *   - 系统角色只有 super-admin 可以修改
     * 
     * @returns Promise<Role> 更新后的完整角色对象
     * 
     * 数据流向：
     * 1. 用户在编辑页面修改字段，点击"保存"
     * 2. 前端调用：roleApi.updateRole('123', { description: '新描述' })
     * 3. 发送请求：PUT /api/v1/roles/123
     *    请求体：{ description: '新描述' }（只包含修改的字段）
     * 4. 后端处理：RoleController@update 方法
     *    - 验证 ID 是否存在
     *    - 检查是否为系统角色（系统角色有权限限制）
     *    - 验证更新数据（使用 UpdateRoleRequest）
     *    - 更新数据库记录
     *    - 如果有 permission_ids，同步权限
     *    - 返回更新后的完整数据
     * 5. 前端处理：
     *    - 显示成功提示
     *    - 更新页面显示的数据
     * 
     * 代码示例：
     * ```typescript
     * // 更新角色的描述
     * const updated = await roleApi.updateRole('123', {
     *   description: '新描述'
     * })
     * 
     * // 更新多个字段
     * const updated2 = await roleApi.updateRole('123', {
     *   name: '新名称',
     *   status: 1,
     *   data_scope: {
     *     scope: 2,  // 自定义数据权限
     *     department_ids: [1, 2, 3]
     *   }
     * })
     * 
     * // 在编辑表单中使用
     * const handleUpdate = async (id: string, formData: UpdateRoleForm) => {
     *   try {
     *     const result = await roleApi.updateRole(id, formData)
     *     message.success('更新成功')
     *     // 更新本地数据
     *     roleData.value = result
     *   } catch (error) {
     *     message.error('更新失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：角色不存在
     * - 422：数据验证失败
     * - 403：无权限修改（如修改系统角色）
     * - 409：冲突（如角色名称重复）
     */
    updateRole(id: string, data: UpdateRoleForm) {
      // PUT 请求，用于完整或部分更新资源
      // 第一个参数：使用函数式路由拼接 ID
      // 第二个参数：更新的数据
      return client.put<Role>(API_ROUTES.role.roles(id), data)
    },
    
    /**
     * 删除角色
     * 
     * 使用场景：
     * - 在角色列表中点击"删除"按钮
     * - 在角色详情页删除当前记录
     * - 清理不再使用的角色
     * 
     * @param id - 要删除的角色的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 数据流向：
     * 1. 用户点击删除按钮，弹出确认对话框
     * 2. 用户确认删除
     * 3. 前端调用：roleApi.deleteRole('123')
     * 4. 发送请求：DELETE /api/v1/roles/123
     * 5. 后端处理：RoleController@destroy 方法
     *    - 验证 ID 是否存在
     *    - 检查是否为系统角色（系统角色不能删除）
     *    - 检查是否有用户使用该角色
     *    - 软删除或硬删除记录
     *    - 返回成功响应
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 从列表中移除该记录
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 删除指定角色
     * await roleApi.deleteRole('123')
     * console.log('删除成功')
     * 
     * // 在列表中使用（带确认）
     * const handleDelete = async (id: string) => {
     *   const confirmed = await showConfirm('确定要删除吗？')
     *   if (!confirmed) return
     *   
     *   try {
     *     await roleApi.deleteRole(id)
     *     message.success('删除成功')
     *     // 刷新列表
     *     await loadList()
     *   } catch (error) {
     *     message.error('删除失败：' + error.message)
     *   }
     * }
     * ```
     * 
     * 错误处理：
     * - 404：角色不存在（可能已被删除）
     * - 403：无权限删除（如删除系统角色）
     * - 400：无法删除（该角色下还有用户）
     *   例如："该角色下还有 5 个用户，无法删除"
     */
    deleteRole(id: string) {
      // DELETE 请求，用于删除资源
      // 通常不返回数据，或返回简单的成功消息
      return client.delete<void>(API_ROUTES.role.delete(id))
    },
    
    // ===== 权限管理 =====
    
    /**
     * 获取角色的权限列表
     * 
     * 使用场景：
     * - 在角色详情页显示已分配的权限
     * - 在权限分配界面显示当前权限
     * - 验证角色是否有某个权限
     * 
     * @param id - 角色ID
     * 
     * @returns Promise<{ role: Role, permissions: string[] }> 角色信息和权限ID数组
     *   返回的数据结构：
     *   {
     *     role: Role,           // 角色完整信息
     *     permissions: string[] // 权限ID数组，如 ['1', '2', '3']
     *   }
     * 
     * 数据流向：
     * 1. 用户打开权限分配对话框
     * 2. 前端调用：roleApi.getRolePermissions('123')
     * 3. 发送请求：GET /api/v1/roles/123/permissions
     * 4. 后端处理：RoleController@permissions 方法
     *    - 查询角色及其关联的权限
     *    - 返回角色信息和权限ID数组
     * 5. 前端处理：
     *    - 在权限树中勾选对应的权限
     *    - 显示角色的权限统计
     * 
     * 代码示例：
     * ```typescript
     * // 获取角色权限
     * const { role, permissions } = await roleApi.getRolePermissions('123')
     * console.log(role.name)        // 角色名称
     * console.log(permissions)      // ['1', '2', '3']
     * 
     * // 在权限树中使用
     * const initPermissionTree = async (roleId: string) => {
     *   const { permissions } = await roleApi.getRolePermissions(roleId)
     *   // 设置树的选中状态
     *   treeRef.value.setCheckedKeys(permissions)
     * }
     * ```
     * 
     * 错误处理：
     * - 404：角色不存在
     * - 403：无权限查看该角色的权限
     */
    getRolePermissions(id: string) {
      return client.get<{
        role: Role
        permissions: string[]
      }>(API_ROUTES.role.permissions(id))
    },
    
    /**
     * 为角色分配权限
     * 
     * 使用场景：
     * - 在权限分配对话框中提交选中的权限
     * - 批量修改角色权限
     * - 权限调整和管理
     * 
     * @param id - 角色ID
     * @param data - 权限ID列表
     *   字段要求（参考 AssignPermissionsForm 类型）：
     *   - permission_ids: number[] - 权限ID数组（必填）
     * 
     * @returns Promise<Role> 更新后的角色信息
     * 
     * 数据流向：
     * 1. 用户在权限树中勾选权限，点击"保存"
     * 2. 前端收集选中的权限ID
     * 3. 前端调用：roleApi.assignRolePermissions('123', { permission_ids: [1, 2, 3] })
     * 4. 发送请求：POST /api/v1/roles/123/permissions
     *    请求体：{ permission_ids: [1, 2, 3] }
     * 5. 后端处理：RoleController@assignPermissions 方法
     *    - 验证 ID 是否存在
     *    - 检查是否为系统角色（系统角色有权限限制）
     *    - 验证权限ID是否存在
     *    - 清除现有权限，分配新权限（syncPermissions）
     *    - 返回更新后的角色信息
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 关闭权限分配对话框
     *    - 刷新角色列表
     * 
     * 代码示例：
     * ```typescript
     * // 为角色分配权限
     * const result = await roleApi.assignRolePermissions('123', {
     *   permission_ids: [1, 2, 3, 4, 5]
     * })
     * console.log(result.permissionCount)  // 权限数量：5
     * 
     * // 在权限分配对话框中使用
     * const handleAssign = async () => {
     *   const checkedKeys = treeRef.value.getCheckedKeys()
     *   const permissionIds = checkedKeys.map(key => Number(key))
     *   
     *   try {
     *     await roleApi.assignRolePermissions(currentRoleId.value, {
     *       permission_ids: permissionIds
     *     })
     *     message.success('权限分配成功')
     *     dialogVisible.value = false
     *     await loadList()
     *   } catch (error) {
     *     message.error('权限分配失败：' + error.message)
     *   }
     * }
     * 
     * // 清除所有权限
     * await roleApi.assignRolePermissions('123', {
     *   permission_ids: []  // 传空数组
     * })
     * ```
     * 
     * 错误处理：
     * - 404：角色不存在
     * - 422：权限ID不存在
     * - 403：无权限修改（如修改系统角色的权限）
     */
    assignRolePermissions(id: string, data: AssignPermissionsForm) {
      return client.post<Role>(API_ROUTES.role.rolesPermissions(id), data)
    },
  }
}

